import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { Input, Button } from '@crazyegginc/hatch';

import { Header } from '../components/ui/Header';

import BlueSpinnerIcon from '@crazyegginc/hatch/dist/animations/spinner-blue.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';
import { ReactComponent as CrossIcon } from '@crazyegginc/hatch/dist/images/icon-cross.svg';

import { checkScriptQuery } from '../../options/queries';
import { DEVICE_TYPES } from '/src/features/_global/constants';
import { normalizeOnboardingSiteUrl, withQueryParams } from '../utils';
import { Footer } from '../components/ui/Footer';
import { RenderResult } from '/src/components/check-script/RenderResult';
import { CheckResultCard } from '../components/ui/Card';
import { useAuthContext, useMutation, useNotifications } from '/src/hooks';
import { addSiteMutation } from '../../options/mutations';
import { createSnapshotsMutation } from '../../snapshots/mutations';
import { upsertUserOnboardingMutation } from '../mutations';
import { completeOnboarding } from '../completion';
import { useOnboardingContext } from '../hooks/useOnboardingContext';
import { isE2E } from '/src/utils';

const MIN_CHECKING_MS = 3000;
const RESULTS_DELAY_MS = 1500;
const CHECK_EXECUTION_MODES = {
  SEQUENTIAL: 'sequential',
  PARALLEL: 'parallel',
};

const DEV_SCRIPT_CHECK_WARNING = {
  name: 'TRACKING_SCRIPT_IN_NON_OPTIMAL_LOCATION',
};

const DEV_SCRIPT_CHECK_INFO = {
  name: 'PAGE_REDIRECTS',
  redirect: 'https://www.example.com/redirected',
};

const DEVICE_CHECK_SEQUENCE = [
  { key: 'desktop', label: 'desktop', device: DEVICE_TYPES.DESKTOP },
  { key: 'tablet', label: 'tablet', device: DEVICE_TYPES.TABLET },
  { key: 'mobile', label: 'mobile', device: DEVICE_TYPES.PHONE },
];

function DeviceCheckRow({ completed, installed, label }) {
  return (
    <div className="flex w-full items-center gap-4 self-center justify-start sm:justify-center">
      <div className="rounded-full shadow w-[48px] h-[49px] flex items-center justify-center flex-shrink-0">
        {completed ? (
          installed ? (
            <TickIcon className="w-5 h-5 text-dodger-blue-500 fill-current" />
          ) : (
            <CrossIcon className="w-5 h-5 text-carnation-500 fill-current" />
          )
        ) : (
          <img src={BlueSpinnerIcon} alt="Loading spinner" className="w-8 h-8 -my-px" />
        )}
      </div>

      <span
        className={classNames(
          `break-words text-2xl font-bold leading-9 text-black-pearl-500 md:text-[32px] md:leading-[48px]`,
          {
            'text-lynch-500': completed,
          },
        )}
      >
        {completed
          ? installed
            ? `Detected tracking script on ${label}`
            : `Missing tracking script on ${label}`
          : `Checking installation on ${label}...`}
      </span>
    </div>
  );
}

function CheckingState({ deviceChecks }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[720px] flex-col gap-10 p-6 sm:py-10 sm:px-6 md:px-0"
      data-state="checking"
    >
      {DEVICE_CHECK_SEQUENCE.map(({ key, label }) => (
        <DeviceCheckRow
          key={key}
          completed={deviceChecks[key].completed}
          installed={deviceChecks[key].installed}
          label={label}
        />
      ))}
    </div>
  );
}

function ScriptCheckResultsState({ deviceChecks, siteUrl, heatmapUrl, rerunChecks }) {
  const queryClient = useQueryClient();
  const [inputUrl, setInputUrl] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const { currentUser } = useAuthContext();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const createSite = useMutation(addSiteMutation);
  const createSnapshots = useMutation(createSnapshotsMutation);
  const { mutateAsync: upsertUserOnboarding } = useMutation(upsertUserOnboardingMutation);

  const { flow, isHeatmapFlow } = useOnboardingContext(currentUser.onboarding.flow);

  const checkUrl = siteUrl || heatmapUrl;
  const normalizedCheckUrl = normalizeOnboardingSiteUrl(checkUrl);
  const completionUrl = isHeatmapFlow ? heatmapUrl || siteUrl : siteUrl || heatmapUrl;

  useEffect(() => {
    setInputUrl(checkUrl);
  }, [checkUrl]);

  const successfulChecks = Object.values(deviceChecks).filter((result) => result.installed).length;

  const dedupedResult = useMemo(() => {
    const completedResults = Object.values(deviceChecks)
      .filter((check) => check.completed && check.result)
      .map((check) => check.result);

    if (completedResults.length === 0) {
      return null;
    }

    const uniqueByContent = (items = []) => {
      const seen = new Set();
      return items.filter((item) => {
        const key = JSON.stringify(item);

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      });
    };

    const installed = completedResults.some((result) => Boolean(result?.installed));
    const error = completedResults.find((result) => result?.error)?.error ?? null;

    return {
      installed,
      error,
      finalUrl: completedResults.find((result) => result?.finalUrl)?.finalUrl ?? null,
      pageTitle: completedResults.find((result) => result?.pageTitle)?.pageTitle ?? null,
      didRedirect: completedResults.some((result) => Boolean(result?.didRedirect)),
      siteVisitedAt: completedResults.find((result) => result?.siteVisitedAt)?.siteVisitedAt ?? null,
      consoleErrors: uniqueByContent(completedResults.flatMap((result) => result?.consoleErrors ?? [])),
      warnings: uniqueByContent(completedResults.flatMap((result) => result?.warnings ?? [])),
      infos: uniqueByContent(completedResults.flatMap((result) => result?.infos ?? [])),
      cookiesSuccessfullySet: uniqueByContent(
        completedResults.flatMap((result) => result?.cookiesSuccessfullySet ?? []),
      ),
    };
  }, [deviceChecks]);
  const dedupedError = useMemo(() => {
    const errors = Object.values(deviceChecks)
      .filter((check) => check.completed && check.requestError)
      .map((check) => check.requestError);

    if (errors.length === 0 || dedupedResult) {
      return null;
    }

    return errors[0];
  }, [dedupedResult, deviceChecks]);
  const isErrorState = Boolean(dedupedError || dedupedResult?.error?.name);

  const handleComplete = useCallback(async () => {
    if (isCompleting) {
      return;
    }

    setIsCompleting(true);
    await completeOnboarding({
      completionUrl,
      isHeatmapFlow,
      flow,
      completedAt: currentUser.onboarding.completedAt,
      createSite,
      createSnapshots,
      upsertUserOnboarding,
      navigate,
      notifications,
      queryClient,
    });
    setIsCompleting(false);
  }, [
    createSite,
    createSnapshots,
    flow,
    isHeatmapFlow,
    isCompleting,
    navigate,
    notifications,
    queryClient,
    completionUrl,
    currentUser.onboarding.completedAt,
    upsertUserOnboarding,
  ]);

  return (
    <>
      <div className="mx-auto flex w-full max-w-[720px] flex-col px-6 md:px-0">
        <div className="flex flex-col sm:items-center gap-2 sm:gap-3 sm:self-center">
          <h1 className="!m-0 !mt-6 sm:!mt-0 mb-4 sm:text-center text-2xl sm:text-[28px] font-bold leading-9 sm:leading-[40px] text-black-pearl-500 md:text-[32px] md:leading-[48px]">
            Check installation
          </h1>
          <p className="sm:text-center text-base sm:text-xl font-normal leading-6 sm:leading-[30px] text-black-pearl-500">
            Check your website to see if Crazy Egg is installed correctly.
          </p>
        </div>

        <div className="flex flex-col gap-5 mt-5 mb-6">
          <div className="flex w-full flex-col items-stretch gap-5 sm:flex-row sm:items-center">
            <Input
              placeholder="https://example.com"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              className="h-[50px] max-w-[720px] w-full placeholder:text-lynch-500 text-base font-normal leading-6 text-black-pearl-500 flex-1"
            />
            <Button
              onClick={() => rerunChecks(inputUrl)}
              disabled={inputUrl.trim().length === 0}
              className="h-[45px] sm:h-[50px] flex items-center justify-center gap-5 px-5 py-[14px] text-base font-bold leading-6 flex-shrink-0"
            >
              Check Installation
            </Button>
          </div>

          <RenderResult
            data={dedupedResult}
            error={dedupedError}
            originalUrl={checkUrl}
            CardComponent={CheckResultCard}
            successTitle="Installation Successful"
            successDescription={() => (
              <span className="!text-sm sm:!text-base !font-normal !leading-6 !text-lynch-500">
                You are all set to start seeing what is working on your site and what is not.
              </span>
            )}
            ui={{
              titleClassName: 'text-base sm:text-lg font-bold leading-[27px] text-black-pearl-500',
              descriptionClassName: 'mt-1 text-sm sm:text-base font-normal leading-[21px] sm:leading-6 text-lynch-500',
              suggestionClassName: 'text-sm sm:text-base mt-2.5 font-normal leading-[21px] sm:leading-6 text-lynch-500',
              suggestionStrongClassName:
                'text-sm sm:text-base font-bold leading-[21px] sm:leading-6 text-black-pearl-500',
              contentClassName:
                '[&_a]:!text-sm sm:[&_a]:!text-base [&_button]:!text-sm sm:[&_button]:!text-base [&_.text-header-4]:!text-base sm:[&_.text-header-4]:!text-lg [&_.text-header-4]:!font-bold [&_.text-header-4]:!leading-[27px] [&_.text-header-4]:!text-black-pearl-500 [&_.text-body-1]:!text-sm sm:[&_.text-body-1]:!text-base [&_.text-body-1]:!font-normal [&_.text-body-1]:!leading-[21px] sm:[&_.text-body-1]:!leading-6 [&_.text-body-1]:!text-lynch-500 [&_.text-body-2]:!text-sm sm:[&_.text-body-2]:!text-base [&_.text-body-2]:!font-normal [&_.text-body-2]:!leading-[21px] sm:[&_.text-body-2]:!leading-6 [&_.text-body-2]:!text-lynch-500 [&_p]:!text-sm sm:[&_p]:!text-base [&_p]:!font-normal [&_p]:!leading-[21px] sm:[&_p]:!leading-6 [&_p]:!text-lynch-500 [&_strong]:!font-bold [&_strong]:!text-black-pearl-500',
              iconContainerClassName: isErrorState
                ? 'mr-[25px] w-10 flex items-start'
                : 'mr-[25px] w-10 flex items-start',
              iconContainerEmptyClassName: 'mr-[25px] w-10 flex items-center',
              iconClassName: 'relative z-10 h-10 w-10',
              iconBackgroundClassName: '!bg-white p-1',
              statusIconClassName: 'h-4 w-4',
              statusIconContainerClassName: '!z-0',
              statusItemClassName: 'text-body-2 relative min-h-[30px]',
              spacerClassName: 'h-10',
              strongClassName: 'text-sm sm:text-base font-bold leading-[21px] sm:leading-6 text-black-pearl-500',
              verticalLineClassName:
                'absolute bottom-[3px] left-1/2 z-0 h-[calc(100%-35px)] w-[6px] -translate-x-1/2 bg-dodger-blue-500/10',
            }}
          />
        </div>

        <Footer
          canBack
          canSkip
          backTo="/onboarding/instructions"
          backUpserts={{ site_url: normalizedCheckUrl || null }}
          nextText={isCompleting ? 'Setting up...' : 'Go to Dashboard'}
          nextOnClick={handleComplete}
          nextProps={{
            disabled: isCompleting || successfulChecks !== Object.keys(deviceChecks).length,
          }}
        />
      </div>
    </>
  );
}

function initialDeviceChecks() {
  return {
    desktop: { completed: false, installed: false, result: null, requestError: null },
    tablet: { completed: false, installed: false, result: null, requestError: null },
    mobile: { completed: false, installed: false, result: null, requestError: null },
  };
}

export function CheckInstallStep() {
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync: upsertUserOnboarding } = useMutation(upsertUserOnboardingMutation);

  const { siteUrl, heatmapUrl } = useOnboardingContext(currentUser.onboarding.flow);
  const targetUrl = siteUrl || heatmapUrl;
  const [isChecking, setIsChecking] = useState(Boolean(targetUrl));
  const [deviceChecks, setDeviceChecks] = useState(initialDeviceChecks);
  const [checkRun, setCheckRun] = useState(0);

  const checkExecutionMode = CHECK_EXECUTION_MODES.PARALLEL;
  const canSimulateCheckResult = import.meta.env.DEV || isE2E();
  const forceSuccess = canSimulateCheckResult && searchParams.get('success') === 'true';
  const forceWarning = canSimulateCheckResult && searchParams.get('warning') === 'true';
  const forceInfo = canSimulateCheckResult && searchParams.get('info') === 'true';
  const queryString = searchParams.toString();

  const rerunChecks = useCallback(
    (nextUrl = targetUrl) => {
      const normalizedUrl = normalizeOnboardingSiteUrl(nextUrl);
      const upserts = {
        site_url: normalizedUrl || null,
      };
      const hasSiteUrlChanged = Boolean(normalizedUrl) && normalizedUrl !== siteUrl;

      navigate(withQueryParams('/onboarding/check-install', queryString, upserts), {
        replace: true,
      });

      if (hasSiteUrlChanged) {
        void upsertUserOnboarding({ siteUrl: normalizedUrl }).catch(() => {
          // noop: URL update should not block checks
        });
      }

      setDeviceChecks(initialDeviceChecks());
      setIsChecking(true);
      setCheckRun((previous) => previous + 1);
    },
    [navigate, queryString, siteUrl, targetUrl, upsertUserOnboarding],
  );

  useEffect(() => {
    if (!targetUrl) {
      setDeviceChecks(initialDeviceChecks());
      setIsChecking(false);
      return;
    }

    let isCancelled = false;
    setIsChecking(true);

    const wait = (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

    const runChecks = async () => {
      const runSingleCheck = async ({ key, device }) => {
        if (isCancelled) {
          return null;
        }

        const startedAt = Date.now();

        let installed = false;
        let result = null;
        let requestError = null;
        try {
          const data = await queryClient.fetchQuery(checkScriptQuery({ device, url: targetUrl }));
          result = data?.checkScript ?? null;
          installed = Boolean(result?.installed);
        } catch (error) {
          installed = false;
          result = null;
          requestError = error;
        }

        if (forceSuccess) {
          installed = true;
          requestError = null;

          result = {
            ...(result ?? {}),
            installed: true,
            error: null,
            finalUrl: result?.finalUrl ?? `https://${targetUrl}`,
            didRedirect: Boolean(result?.didRedirect),
            warnings: forceWarning ? [DEV_SCRIPT_CHECK_WARNING] : (result?.warnings ?? []),
            infos: forceInfo ? [DEV_SCRIPT_CHECK_INFO] : (result?.infos ?? []),
          };
        }

        const elapsedTime = Date.now() - startedAt;
        const remainingTime = Math.max(0, MIN_CHECKING_MS - elapsedTime);
        await wait(remainingTime);

        return { key, installed, result, requestError };
      };

      if (checkExecutionMode === CHECK_EXECUTION_MODES.PARALLEL) {
        await Promise.all(
          DEVICE_CHECK_SEQUENCE.map(async (entry) => {
            const checkResult = await runSingleCheck(entry);

            if (isCancelled || !checkResult) {
              return;
            }

            const { key, installed, result, requestError } = checkResult;

            setDeviceChecks((previous) => ({
              ...previous,
              [key]: { completed: true, installed, result, requestError },
            }));
          }),
        );
      } else {
        for (const entry of DEVICE_CHECK_SEQUENCE) {
          const checkResult = await runSingleCheck(entry);

          if (isCancelled || !checkResult) {
            return;
          }

          const { key, installed, result, requestError } = checkResult;

          setDeviceChecks((previous) => ({
            ...previous,
            [key]: { completed: true, installed, result, requestError },
          }));
        }
      }

      if (isCancelled) {
        return;
      }

      await wait(RESULTS_DELAY_MS);

      if (isCancelled) {
        return;
      }

      setIsChecking(false);
    };

    runChecks();

    return () => {
      isCancelled = true;
    };
  }, [checkExecutionMode, checkRun, forceInfo, forceSuccess, forceWarning, queryClient, targetUrl]);

  const allChecksSuccessful = Object.values(deviceChecks).every((result) => result.installed);

  return (
    <>
      <Header
        currentStep={isChecking ? 2 : 3}
        totalSteps={3}
        hasError={!!siteUrl && !isChecking && !allChecksSuccessful}
      />

      {isChecking ? (
        <CheckingState deviceChecks={deviceChecks} />
      ) : (
        <ScriptCheckResultsState
          deviceChecks={deviceChecks}
          siteUrl={siteUrl}
          heatmapUrl={heatmapUrl}
          rerunChecks={rerunChecks}
        />
      )}
    </>
  );
}
